import { Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CourseApi, ICourse } from '../api/CourseApi';
import { DialogApi, IDialog } from '../api/DialogApi';
import { ILesson, LessonApi } from '../api/LessonApi';
import { handleErrorMessage } from '../component/ErrorDialog';
import AppBarContext from '../context/AppBarContext';

interface DialogFormProps {
    dialogId: number;
    onSubmit: (updatedDialog: IDialog) => void;
    onDelete: (dialogId: number) => void;
    isSavingOrDeleting: boolean
}

const DialogForm: React.FC<DialogFormProps> = ({ dialogId, onSubmit, onDelete, isSavingOrDeleting }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const [id, setId] = React.useState(-1);

    const [lessonId, setLessonId] = React.useState(-1);
    const [courseDictionary, setCourseDictionary] = React.useState<{ [key: number]: ICourse; }>({}); // State to store course options
    const [lessonList, setLessonList] = React.useState<ILesson[]>([]);
    const [title, setTitle] = React.useState('');
    const [subtitle, setSubtitle] = React.useState<string | undefined>(undefined);
    const [isHidden, setIsHidden] = React.useState(false);
    const [orderIndex, setOrderIndex] = React.useState(-1);
    const [showConfirmation, setShowConfirmation] = React.useState(false);

    React.useEffect(() => {
        const fetchCourseList = async () => {
            try {
                const response = await CourseApi.getAll();
                const courseList = response.data

                const courseDictionary = courseList.reduce((accumulator, currentCourse) => {
                    accumulator[currentCourse.id] = currentCourse;
                    return accumulator;
                }, {} as { [key: number]: typeof courseList[number] }); // Using "typeof courseList[number]" to infer course type
                setCourseDictionary(courseDictionary)

            } catch (err: any) {
                handleErrorMessage(err, setErrorMessage, history, location);
            }
        };
        const fetchLessonList = async () => {
            try {
                const response = await LessonApi.getAll();
                setLessonList(response.data);
            } catch (err: any) {
                handleErrorMessage(err, setErrorMessage, history, location);
            }
        };
        fetchCourseList();
        fetchLessonList();
    }, [history, location, setErrorMessage]);

    React.useEffect(() => {
        DialogApi.getById(dialogId)
            .then((response) => {
                const dialog = response.data;
                setId(dialog.id);
                setLessonId(dialog.lesson_id);
                setTitle(dialog.title);
                setSubtitle(dialog.subtitle);
                setIsHidden(dialog.is_hidden);
                setOrderIndex(dialog.order_index);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [dialogId, history, location, setErrorMessage]);

    const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => { setId(Number(e.target.value)); };
    const handleLessonIdChange = (e: React.ChangeEvent<HTMLInputElement>) => { setLessonId(Number(e.target.value)); };
    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => { setTitle(e.target.value); };
    const handleSubtitleChange = (e: React.ChangeEvent<HTMLInputElement>) => { setSubtitle(e.target.value); };
    const handleHiddenChange = (e: React.ChangeEvent<HTMLInputElement>) => { setIsHidden(e.target.checked); };
    const handleOrderIndexChange = (e: React.ChangeEvent<HTMLInputElement>) => { setOrderIndex(Number(e.target.value)); };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const updatedDialog: IDialog = {
            id,
            lesson_id: lessonId,
            order_index: orderIndex,
            title,
            subtitle,
            is_hidden: isHidden
        };
        onSubmit(updatedDialog);
    };


    const handleDeleteConfirmation = () => {
        setShowConfirmation(true);
    };

    const handleDeleteCancel = () => {
        setShowConfirmation(false);
    };

    const handleDeleteConfirm = () => {
        setShowConfirmation(false);
        onDelete(dialogId);
    };

    return (
        <form onSubmit={handleSubmit}>
            <Stack spacing={4} sx={{ width: '90%', margin: '0 auto', pt: 2, pb: 4 }}>
                <Typography variant="h5">
                    Edit Dialog
                </Typography>
                <input type="hidden" value={id} onChange={handleIdChange} />
                <input type="hidden" value={lessonId} onChange={handleLessonIdChange} />
                <TextField
                    label="Title"
                    value={title}
                    onChange={handleTitleChange}
                    required
                />
                <TextField
                    label="Subtitle"
                    value={subtitle}
                    onChange={handleSubtitleChange}
                />
                <FormControl fullWidth>
                    <InputLabel>Parent Lesson Name</InputLabel>
                    <Select
                        value={lessonId}
                        label="Parent Lesson Name"
                        onChange={e => setLessonId(Number(e.target.value))}
                        required
                    >
                        <MenuItem key={-1} value={-1} />
                        {lessonList.map((lesson) => (
                            <MenuItem key={lesson.id} value={lesson.id} sx={{ fontWeight: lesson.is_hidden ? 200 : 400, opacity: lesson.is_hidden ? 0.4 : 1 }}>
                                {lesson.is_hidden && "[Hidden]"} {courseDictionary[lesson.course_id] ? courseDictionary[lesson.course_id].name : "Course Deleted"} :: {lesson.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={<Checkbox checked={isHidden} onChange={handleHiddenChange} />}
                    label="Hidden"
                />
                <TextField
                    label="Sequence Number"
                    value={orderIndex}
                    onChange={handleOrderIndexChange}
                    type="number"
                    required
                />
                <br />
                <Button disabled={isSavingOrDeleting} type="submit" variant="contained">
                    {isSavingOrDeleting ? <CircularProgress size={24} /> : 'Save'}
                </Button>
                <Button disabled={isSavingOrDeleting} variant="contained" color="error" onClick={handleDeleteConfirmation}>
                    {isSavingOrDeleting ? <CircularProgress size={24} /> : 'Delete'}
                </Button>
            </Stack>
            <Dialog open={showConfirmation} onClose={handleDeleteCancel}>
                <DialogTitle>Delete Dialog</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this dialog?</Typography>
                    <Typography>{title}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </form>
    );
};

export default DialogForm;
